import { ArrowDown, ArrowRight, Factory, PackageSearch, Search, ShoppingBag, Truck } from "lucide-react";
import { useState } from "react";
import { ApiError, apiRequest } from "../api";
import { formatDate, formatNumber, Message, PageHeader } from "../components/Ui";

type TraceResult = {
  query: string;
  found: boolean;
  lots: Array<{
    id: string; code: string; itemCode: string; itemName: string;
    itemType: string; supplierName?: string | null; purchaseOrderNumber?: string | null;
    manufactureDate?: string | null; expiryDate?: string | null; qualityStatus: string;
  }>;
  materialIssues: Array<{
    id: string; number: string; documentDate: string; targetProductionLotCode?: string | null; warehouseName: string;
    lines: Array<{ code: string; name: string; lotCode?: string | null; quantity: number }>;
  }>;
  productionReports: Array<{
    number: string; reportDate: string; productionLotCode: string; productCode: string; productName: string;
    productionLineName?: string | null; shiftName?: string | null; machineQuantity: number;
    semiFinishedQuantity: number; finishedQuantity: number; rejectedQuantity: number;
  }>;
  productFlow: Array<{
    number: string; documentType: "Receipt" | "Issue"; reason: string; documentDate: string;
    warehouseName: string; partnerName?: string | null;
    lines: Array<{ code: string; name: string; lotCode: string; quantity: number }>;
  }>;
  summary: {
    productionLots: string[];
    totalFinished: number;
    totalReceived: number;
    totalSold: number;
    customers: string[];
  };
};

export function TraceabilityPage() {
  const [query, setQuery] = useState("");
  const [data, setData] = useState<TraceResult | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!query.trim()) return setError("Hãy nhập số lô cần truy xuất.");
    setLoading(true);
    setError("");
    try {
      setData(await apiRequest<TraceResult>(`/api/traceability?lotCode=${encodeURIComponent(query.trim())}`));
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không truy xuất được số lô.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="module-page">
      <PageHeader
        eyebrow="TRUY NGƯỢC VÀ TRUY XUÔI"
        title="Truy xuất theo số lô"
        description="Luồng bắt đầu từ Đơn mua và đi xuyên suốt nhập nguyên liệu/bao bì, xuất sản xuất, báo cáo sản xuất, nhập thành phẩm và xuất bán."
      />
      <section className="trace-search-card">
        <PackageSearch size={31} />
        <div><strong>Nhập số lô thành phẩm hoặc lô nhà cung cấp</strong><span>Có thể tìm bằng lô in trên hộp, lô nguyên liệu hoặc lô thành phẩm.</span></div>
        <label><Search size={18} /><input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void search(); }} placeholder="Ví dụ: LOT-20260729-01" /></label>
        <button className="primary-button" disabled={loading} onClick={() => void search()}>{loading ? "Đang tìm..." : "Truy xuất"}</button>
      </section>
      {error && <Message tone="error">{error}</Message>}
      {data && !data.found && <section className="panel empty-trace"><PackageSearch size={42} /><h3>Chưa tìm thấy lô {data.query}</h3><p>Kiểm tra lại số lô hoặc hoàn tất các chứng từ nhập/xuất liên quan.</p></section>}
      {data?.found && (
        <>
          <section className="trace-summary">
            <article><span>Lô liên quan</span><strong>{data.summary.productionLots.join(", ")}</strong></article>
            <article><span>Thành phẩm sản xuất</span><strong>{formatNumber(data.summary.totalFinished)}</strong></article>
            <article><span>Đã nhập kho</span><strong>{formatNumber(data.summary.totalReceived)}</strong></article>
            <article><span>Đã xuất bán</span><strong>{formatNumber(data.summary.totalSold)}</strong></article>
          </section>
          <div className="trace-flow-labels">
            <span><ShoppingBag size={17} /> Đơn mua & nhập lô</span><ArrowRight size={18} />
            <span><Truck size={17} /> Xuất cho xưởng</span><ArrowRight size={18} />
            <span><Factory size={17} /> Báo cáo sản xuất</span><ArrowRight size={18} />
            <span><PackageSearch size={17} /> Nhập & xuất thành phẩm</span>
          </div>

          <TraceSection title="1. Nguồn lô và nhà cung cấp">
            <table className="data-table"><thead><tr><th className="stt-column">STT</th><th>Mã hàng</th><th>Tên hàng</th><th>Số lô</th><th>Nhà cung cấp</th><th>Đơn mua</th><th>Hạn sử dụng</th></tr></thead><tbody>
              {data.lots.map((row, index) => <tr key={row.id}><td className="stt-column">{index + 1}</td><td><strong>{row.itemCode}</strong></td><td>{row.itemName}</td><td><strong>{row.code}</strong></td><td>{row.supplierName || "—"}</td><td>{row.purchaseOrderNumber || "—"}</td><td>{formatDate(row.expiryDate)}</td></tr>)}
              {!data.lots.length && <tr><td colSpan={7}>Số lô được tìm thấy qua liên kết sản xuất, chưa có bản ghi nguồn riêng.</td></tr>}
            </tbody></table>
          </TraceSection>

          <div className="trace-down"><ArrowDown size={22} /></div>
          <TraceSection title="2. Nguyên liệu và bao bì xuất cho sản xuất">
            {data.materialIssues.map((doc) => <article className="trace-document" key={doc.id}><header><strong>{doc.number}</strong><span>{formatDate(doc.documentDate)} · Lô thành phẩm: {doc.targetProductionLotCode}</span></header><div className="data-table-wrapper"><table className="data-table compact"><thead><tr><th className="stt-column">STT</th><th>Mã</th><th>Tên hàng</th><th>Số lô</th><th>Số lượng</th></tr></thead><tbody>{doc.lines.map((line, index) => <tr key={`${doc.id}-${index}`}><td className="stt-column">{index + 1}</td><td>{line.code}</td><td>{line.name}</td><td>Lô: <strong>{line.lotCode}</strong></td><td>{formatNumber(line.quantity)}</td></tr>)}</tbody></table></div></article>)}
            {!data.materialIssues.length && <div className="empty-inline">Chưa có phiếu xuất nguyên liệu liên quan.</div>}
          </TraceSection>

          <div className="trace-down"><ArrowDown size={22} /></div>
          <TraceSection title="3. Báo cáo sản xuất theo lô">
            <div className="data-table-wrapper"><table className="data-table"><thead><tr><th className="stt-column">STT</th><th>Báo cáo</th><th>Ngày / Ca</th><th>Sản phẩm</th><th>Số lô</th><th>Trên máy</th><th>Bán TP</th><th>Thành phẩm</th><th>Lỗi</th></tr></thead><tbody>{data.productionReports.map((row, index) => <tr key={row.number}><td className="stt-column">{index + 1}</td><td>{row.number}</td><td>{formatDate(row.reportDate)}<small className="table-subtext">{row.shiftName}</small></td><td>{row.productCode} – {row.productName}</td><td><strong>{row.productionLotCode}</strong></td><td>{formatNumber(row.machineQuantity)}</td><td>{formatNumber(row.semiFinishedQuantity)}</td><td><strong>{formatNumber(row.finishedQuantity)}</strong></td><td>{formatNumber(row.rejectedQuantity)}</td></tr>)}</tbody></table></div>
          </TraceSection>

          <div className="trace-down"><ArrowDown size={22} /></div>
          <TraceSection title="4. Nhập kho, tồn và xuất bán cho khách hàng">
            <div className="data-table-wrapper"><table className="data-table"><thead><tr><th className="stt-column">STT</th><th>Phiếu</th><th>Ngày</th><th>Nghiệp vụ</th><th>Kho</th><th>Sản phẩm / Lô</th><th>Số lượng</th><th>Khách hàng</th></tr></thead><tbody>{data.productFlow.flatMap((doc) => doc.lines.map((line) => ({ doc, line }))).map(({ doc, line }, index) => <tr key={`${doc.number}-${index}`}><td className="stt-column">{index + 1}</td><td>{doc.number}</td><td>{formatDate(doc.documentDate)}</td><td>{doc.reason === "SalesIssue" ? "Xuất bán" : doc.documentType === "Receipt" ? "Nhập kho" : "Xuất kho"}</td><td>{doc.warehouseName}</td><td>{line.code} – {line.name}<small className="table-subtext">Lô {line.lotCode}</small></td><td>{formatNumber(line.quantity)}</td><td><strong>{doc.partnerName || "—"}</strong></td></tr>)}</tbody></table></div>
            {!!data.summary.customers.length && <div className="customer-result"><strong>Khách hàng đã nhận lô:</strong> {data.summary.customers.join(", ")}</div>}
          </TraceSection>
        </>
      )}
    </div>
  );
}

function TraceSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="panel trace-section"><h3>{title}</h3>{children}</section>;
}
